import { motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const FloatingTechIcons = () => {
  const icons = [
    { name: 'React', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
    { name: 'Vue', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg' },
    { name: 'Angular', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg' },
    { name: 'Node', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
    { name: 'AWS', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg' },
    { name: 'Docker', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' }
  ];

  return (
    <>
      {icons.map((icon, index) => {
        const angle = (index / icons.length) * Math.PI * 2;
        const radius = 300;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        return (
          <motion.div
            key={icon.name}
            className="absolute w-12 h-12 bg-white rounded-xl shadow-lg p-2"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{
              x: x,
              y: y,
              opacity: 1,
              rotate: [0, 360],
            }}
            transition={{
              duration: 20,
              delay: index * 0.5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear"
            }}
          >
            <img src={icon.icon} alt={icon.name} className="w-full h-full" />
            <div className="absolute inset-0 bg-gradient-to-r from-neon-orange/0 via-neon-orange/30 to-neon-orange/0 animate-shimmer" />
          </motion.div>
        );
      })}
    </>
  );
};

const MountainPeak = () => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start("visible");
  }, [controls]);

  return (
    <div className="relative w-full h-full">
      {/* Cercle lumineux central */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.5 }}
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-orange/20 via-neon-orange/40 to-neon-orange/20 animate-pulse" />
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-orange/10 via-white/20 to-neon-orange/10 animate-ping" />
      </motion.div>

      {/* Icônes technologiques flottantes */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <FloatingTechIcons />
      </div>

      {/* SVG principal */}
      <svg viewBox="0 0 1200 800" className="w-full h-full">
        <defs>
          {/* Gradient radial orange */}
          <radialGradient id="orangeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF6A00" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#FF6A00" stopOpacity="0" />
          </radialGradient>

          {/* Gradient pour effet néon */}
          <filter id="neonEffect">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feFlood floodColor="#FF6A00" floodOpacity="0.5" />
            <feComposite in2="blur" operator="in" />
            <feComposite in="SourceGraphic" />
          </filter>

          {/* Pattern hexagonal */}
          <pattern id="hexPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <path
              d="M20 1L37.3205 10.5V29.5L20 39L2.67949 29.5V10.5L20 1Z"
              fill="none"
              stroke="#FF6A00"
              strokeOpacity="0.1"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>

        {/* Fond avec pattern */}
        <rect width="1200" height="800" fill="url(#hexPattern)" />

        {/* Cercles lumineux */}
        {[...Array(20)].map((_, i) => (
          <motion.circle
            key={i}
            cx={Math.random() * 1200}
            cy={Math.random() * 800}
            r={Math.random() * 100 + 50}
            fill="url(#orangeGlow)"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}

        {/* Lignes connectées */}
        {[...Array(10)].map((_, i) => {
          const points = [...Array(3)].map(() => ({
            x: Math.random() * 1200,
            y: Math.random() * 800
          }));
          
          return (
            <motion.path
              key={i}
              d={`M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`}
              stroke="#FF6A00"
              strokeWidth="2"
              strokeOpacity="0.3"
              fill="none"
              filter="url(#neonEffect)"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{
                pathLength: [0, 1],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.5
              }}
            />
          );
        })}

        {/* Particules flottantes */}
        {[...Array(50)].map((_, i) => (
          <motion.circle
            key={i}
            r={Math.random() * 3 + 1}
            fill="#FF6A00"
            initial={{
              x: Math.random() * 1200,
              y: Math.random() * 800,
              opacity: 0
            }}
            animate={{
              x: Math.random() * 1200,
              y: Math.random() * 800,
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </svg>

      {/* Overlay avec effet de profondeur */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-orange/5 to-neon-orange/10" />
    </div>
  );
};

const Summit = () => {
  return (
    <section className="relative py-12 md:py-20 bg-gradient-to-b from-gray-900 to-black overflow-hidden">
      {/* Effet de parallaxe */}
      <motion.div
        className="absolute inset-0 opacity-30"
        initial={{ y: 0 }}
        animate={{ y: [-20, 20, -20] }}
        transition={{ duration: 10, repeat: Infinity }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--neon-orange)_0%,_transparent_100%)] opacity-20" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6 md:space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative inline-block"
            >
              <span className="relative z-10 text-neon-orange font-bold text-base md:text-xl px-6 md:px-8 py-2 md:py-3 bg-neon-orange/10 rounded-full border border-neon-orange/20">
                NE CHERCHEZ PLUS !
              </span>
              <motion.div
                className="absolute inset-0 bg-neon-orange/20 rounded-full blur-xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity
                }}
              />
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-6xl font-bold leading-tight"
            >
              <span className="bg-gradient-to-r from-white via-neon-orange to-white bg-clip-text text-transparent">
                Atteignez de nouveaux sommets
              </span>
              <br />
              <span className="bg-gradient-to-r from-neon-orange to-white bg-clip-text text-transparent">
                avec Couteau Suisse Digital !
              </span>
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-gray-300 text-base md:text-xl leading-relaxed"
            >
              Nous sommes une agence spécialisée dans les nouvelles technologies, 
              avec une expertise éprouvée dans des domaines tels que la banque et la santé. 
              Notre équipe passionnée et expérimentée est prête à mettre à profit nos 
              connaissances et notre réseau pour vous aider à réaliser des résultats exceptionnels.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col md:flex-row gap-4 md:gap-6"
            >
              <Link
                to="/contact"
                className="relative overflow-hidden bg-gradient-to-r from-neon-orange to-[#FF4500] text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg shadow-lg w-full md:w-auto"
              >
                <span className="relative z-10">Contactez-nous</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 1 }}
                />
              </Link>
              
              <Link
                to="/reserver"
                className="relative overflow-hidden border-2 border-neon-orange text-neon-orange px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg w-full md:w-auto"
              >
                <span className="relative z-10">Appelez-nous</span>
                <motion.div
                  className="absolute inset-0 bg-neon-orange/10"
                  initial={{ scale: 0 }}
                  whileHover={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative h-[400px] md:h-[800px]"
          >
            <MountainPeak />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Summit;